import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  CircleNotch,
  ChartBar,
  CurrencyDollar,
  Users,
  FileText,
  ClipboardText,
  ArrowUp,
  ArrowDown,
  Package,
  Warning
} from '@phosphor-icons/react';


function StatCard({ title, value, subtitle, icon: Icon, color, trend }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500">{title}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
}

export default function ReportsPage() {
  const [period, setPeriod] = useState('month');
  const [salesReport, setSalesReport] = useState(null);
  const [workOrdersReport, setWorkOrdersReport] = useState(null);
  const [profitabilityReport, setProfitabilityReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, [period]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [sales, workOrders, profitability, inventory] = await Promise.all([
        axios.get(`${API_URL}/api/reports/sales?period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/work-orders?period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/profitability?period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/inventory`, { withCredentials: true })
      ]);
      
      setSalesReport(sales.data);
      setWorkOrdersReport(workOrders.data);
      setProfitabilityReport(profitability.data);
      setInventoryReport(inventory.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reportes</h1>
          <p className="text-slate-500">Análisis de ventas, rentabilidad y operaciones</p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white font-medium"
          data-testid="period-select"
        >
          <option value="week">Última semana</option>
          <option value="month">Este mes</option>
          <option value="quarter">Este trimestre</option>
          <option value="year">Este año</option>
        </select>
      </div>

      {/* Sales Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Cotizaciones"
          value={salesReport?.total_quotes || 0}
          icon={FileText}
          color="bg-blue-600"
        />
        <StatCard
          title="Pagos Recibidos"
          value={`$${(salesReport?.payments?.total_received || 0).toLocaleString()}`}
          subtitle={`${salesReport?.payments?.count || 0} transacciones`}
          icon={CurrencyDollar}
          color="bg-green-600"
        />
        <StatCard
          title="Nuevos Clientes"
          value={salesReport?.new_clients || 0}
          icon={Users}
          color="bg-purple-600"
        />
        <StatCard
          title="Tasa de Conversión"
          value={`${salesReport?.conversion_rate || 0}%`}
          icon={ChartBar}
          color="bg-orange-500"
        />
      </div>

      {/* Quotes by Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-lg mb-4">Cotizaciones por Estado</h3>
          <div className="space-y-3">
            {Object.entries(salesReport?.quotes_by_status || {}).map(([status, data]) => (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    status === 'Paid' ? 'bg-green-500' :
                    status === 'Signed' ? 'bg-blue-500' :
                    status === 'Sent' ? 'bg-yellow-500' :
                    status === 'Approved' ? 'bg-purple-500' :
                    'bg-slate-400'
                  }`}></div>
                  <span className="text-slate-700">{status}</span>
                </div>
                <div className="text-right">
                  <span className="font-semibold">{data.count}</span>
                  <span className="text-slate-500 text-sm ml-2">
                    ${data.value?.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Work Orders Stats */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-lg mb-4">Órdenes de Trabajo</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{workOrdersReport?.total_work_orders || 0}</p>
              <p className="text-sm text-slate-500">Total Órdenes</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{workOrdersReport?.total_hours || 0}h</p>
              <p className="text-sm text-slate-500">Horas Trabajadas</p>
            </div>
          </div>
          <div className="space-y-2">
            {Object.entries(workOrdersReport?.by_status || {}).map(([status, data]) => (
              <div key={status} className="flex items-center justify-between text-sm">
                <span className="text-slate-600 capitalize">{status.replace('_', ' ')}</span>
                <span className="font-medium">{data.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Profitability */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-lg mb-4">Rentabilidad por Proyecto</h3>
        {profitabilityReport?.projects?.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Orden</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Cliente</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-500">Ingresos</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-500">Materiales</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-500">Mano Obra</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-500">Ganancia</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-500">Margen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {profitabilityReport.projects.map((project, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{project.work_order}</td>
                      <td className="px-4 py-3 text-slate-600">{project.client}</td>
                      <td className="px-4 py-3 text-right">${project.revenue?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-slate-600">${project.material_cost?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-slate-600">${project.labor_cost?.toLocaleString()}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${project.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${project.profit?.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold ${project.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {project.margin}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 font-semibold">
                  <tr>
                    <td colSpan="2" className="px-4 py-3">TOTAL</td>
                    <td className="px-4 py-3 text-right">${profitabilityReport.summary?.total_revenue?.toLocaleString()}</td>
                    <td colSpan="2" className="px-4 py-3 text-right">${profitabilityReport.summary?.total_cost?.toLocaleString()}</td>
                    <td className={`px-4 py-3 text-right ${profitabilityReport.summary?.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${profitabilityReport.summary?.total_profit?.toLocaleString()}
                    </td>
                    <td className={`px-4 py-3 text-right ${profitabilityReport.summary?.average_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {profitabilityReport.summary?.average_margin}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-slate-500">
            No hay proyectos completados en este período
          </div>
        )}
      </div>

      {/* Inventory & Technician Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inventory Report */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-lg mb-4">Resumen de Inventario</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{inventoryReport?.totals?.total_items || 0}</p>
              <p className="text-sm text-slate-500">Productos</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">
                ${(inventoryReport?.totals?.total_cost_value || 0).toLocaleString()}
              </p>
              <p className="text-sm text-slate-500">Valor en Stock</p>
            </div>
          </div>
          
          {inventoryReport?.low_stock_count > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-orange-800">
                <Warning size={20} />
                <span className="font-medium">{inventoryReport.low_stock_count} productos con stock bajo</span>
              </div>
            </div>
          )}

          <h4 className="text-sm font-semibold text-slate-500 uppercase mb-3">Por Categoría</h4>
          <div className="space-y-2">
            {inventoryReport?.by_category?.map((cat) => (
              <div key={cat._id} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{cat._id}</span>
                <div className="text-right">
                  <span className="font-medium">{cat.items} items</span>
                  <span className="text-slate-400 ml-2">${cat.total_value?.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technician Performance */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-lg mb-4">Desempeño por Técnico</h3>
          {workOrdersReport?.by_technician?.length > 0 ? (
            <div className="space-y-4">
              {workOrdersReport.by_technician.map((tech) => (
                <div key={tech._id} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{tech.name}</span>
                    <span className="text-sm text-slate-500">{tech.count} órdenes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 rounded-full"
                        style={{ width: `${Math.min((tech.total_hours / (tech.count * 8)) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{tech.total_hours}h</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No hay datos de técnicos en este período
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
